<script setup lang="ts">
import LoaderButton from '@/components/display/button/LoaderButton.vue';
import { EventBus } from '@/assets/js/event-bus';

interface Button {
  label: string;
  action?: () => void;
  nav?: string;
  style?: string;
  icon?: string;
  tooltip?: string;
  disabled?: boolean;
  dis?: boolean;
  actionStatus?: string;
}

interface Heading {
  title?: string;
  bgColor?: string;
  color?: string;
  icon?: string;
  customIcon?: string;
  expandShow?: boolean;
  buttonSave?: boolean;
}

interface Props {
  buttons?: Button[];
  actionStatus?: string;
  isVerticalView?: boolean;
  customIcon?: string;
  modalId?: string;
  heading?: Heading;
  showClose?: boolean;
  resizeModal?: boolean;
  noCloseBtn?: boolean;
  bottomLine?: boolean;
}

interface Emits {
  (e: 'savetext'): void;
}

const props = withDefaults(defineProps<Props>(), {
  heading: () => ({}),
  showClose: true,
  resizeModal: false,
  noCloseBtn: false,
  bottomLine: true,
});

const emit = defineEmits<Emits>();
</script>

<template>
  <div
    class="uk-modal-dialog uk-modal-body"
    :class="{
      'uk-padding-remove': props.heading?.title,
      'resize-modal': props.resizeModal,
    }"
  >
    <div
      v-if="props.isVerticalView"
      id="modalHeader"
      class="uk-flex uk-flex-right header uk-modal-body"
      :style="`background: ${props.heading?.bgColor ? props.heading.bgColor : '#2C3157'}; padding: 30px`"
    >
      <span style="margin-left: -10px">
        <img src="/icons/white/icon-white-ExpandThread.svg" alt="" />
        <span style="color: white; font-size: 120%; padding-left: 8px">Threads View</span>
      </span>
      <span
        uk-icon="icon: close; ratio: 1.2"
        class="uk-modal-close"
        style="color: white; cursor: pointer; margin-right: -15px"
      ></span>
    </div>
    <div
      v-if="props.heading?.title"
      class="uk-flex uk-flex-right header uk-modal-body"
      :id="props.modalId"
      :style="`background: ${props.heading?.bgColor ? props.heading.bgColor : '#2C3157'}; padding: 18px`"
    >
      <div class="uk-flex header">
        <span
          v-if="props.heading?.icon"
          class="uk-flex-grow uk-margin-small-right uk-icon"
          :uk-icon="`icon: ${props.heading.icon}; ratio: 1.5`"
        />
        <img
          v-else-if="props.heading?.customIcon"
          :src="props.heading.customIcon"
          width="25"
          class="box-heading-img"
        />
        <h3
          :style="{ color: props.heading?.color || 'white' }"
          class="uk-text-bold uk-margin-remove-vertical uk-margin-small-left ellipsis-link"
        >
          {{ props.heading?.title }}
        </h3>
      </div>

      <div v-if="props.showClose" class="uk-flex uk-flex-right">
        <span
          v-if="props.heading?.expandShow"
          uk-icon="icon: expand; ratio:1"
          class="expand-icon-preview"
          @click="EventBus.emit('citation.preview')"
        ></span>
        <button
          v-if="props.heading?.buttonSave"
          class="uk-button uk-border-rounded uk-margin-right"
          @click="emit('savetext')"
        >
          Save
        </button>
        <button
          v-if="props.customIcon"
          :uk-icon="`icon: ${props.customIcon}; ratio: 1.3`"
          class="uk-modal-close"
          style="cursor: pointer"
        ></button>
        <button
          v-else-if="!props.noCloseBtn"
          class="uk-modal-close"
          :uk-icon="`icon: ${'close'}; ratio: 1.3`"
          type="button"
          :style="{ color: props.heading?.color || 'white' }"
        ></button>
      </div>
    </div>
    <div v-else-if="props.showClose" class="uk-flex uk-flex-right uk-margin-bottom">
      <button
        v-if="props.customIcon"
        :uk-icon="`icon: ${props.customIcon}; ratio: 1.3`"
        class="uk-modal-close"
        style="cursor: pointer"
      ></button>
      <button v-else-if="!props.noCloseBtn" class="uk-modal-close" type="button" uk-close></button>
    </div>
    <div :class="{ 'uk-modal-body': props.heading?.title }">
      <slot></slot>
    </div>
    <div
      v-if="props.buttons"
      class="uk-modal-footer uk-flex uk-flex-between"
      :class="{ 'uk-border-rounded': props.heading?.title }"
      :style="{
        border: !props.bottomLine && 'none',
      }"
    >
      <template v-for="(button, idx) in props.buttons" :key="idx">
        <LoaderButton
          v-if="button.action"
          class="capitalize"
          :class="{
            'uk-margin-small-left': idx > 0,
          }"
          :uk-tooltip="button.tooltip"
          :type="button.style ? button.style : 'default'"
          :icon="button.icon"
          :loaderStatus="
            button.style === 'primary' || button.style === 'orange'
              ? props.actionStatus
              : button.actionStatus
                ? props.actionStatus
                : ''
          "
          :disabled="button.disabled || button.dis"
          @click="button.action"
        >
          {{ button.label }}
        </LoaderButton>
        <a
          v-else-if="button.nav"
          :key="idx"
          class="uk-button uk-flex uk-flex-middle uk-border-rounded capitalize"
          :class="{
            [`uk-button-${button.style ? button.style : 'default'}`]: true,
            'uk-margin-small-left': idx > 0,
          }"
          :href="button.nav"
          :disabled="button.disabled"
          uk-toggle
        >
          {{ button.label }}
        </a>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.header {
  height: 60px;
  justify-content: space-between;
  align-items: center;
}
.resize-modal {
  resize: horizontal;
  overflow: hidden;
  min-width: 845px;
  width: 845px;
}

#modalHeader {
  position: sticky;
  top: 0;
  z-index: 999;
  padding: 12px;
  color: #2c3157;
}
.box-heading-img {
  margin-top: 4px;
}
</style>
